import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { createContext, forwardRef, useMemo } from 'react';
import clsx from 'clsx';
import 'tabbable';
import { clamp, equal, toTopLeftPercent } from '../../utils/vector.js';
import { useDragV2 } from '../../hooks/use-drag/use-drag-v2.js';
import { useSharedRef } from '../../hooks/use-shared-ref.js';
import { useValidatedContext } from '../../hooks/use-validated-context.js';
import { useString } from '../../settings/settings.strings.js';
import { useSlider2DKeyboard } from './slider-2d-primitive.hooks.js';
import { resolveVector, computePercent, computePercentFromRect, computeValueRaw, computeValue } from './slider-2d-primitive.utils.js';
import { root, hiddenInput, hiddenInputSrOnly, thumb } from './slider-2d-primitive.css.js';

const Slider2DContext = createContext(null);
/**
 * A two-dimensional slider for controlling two values simultaneously on a plane.
 *
 * Uses two visually-hidden `<input type="range">` elements for screen reader accessibility,
 * enforcing the idea that there are two distinct values being controlled.
 *
 * The root is the drag surface. Children (like Thumb) are positioned within it.
 */
const Root = forwardRef(function Root({ className, children, value: valueIn, min: minIn = 0, max: maxIn = 1, step: stepIn = 0.01, bigStep: bigStepIn = 0.1, precision = 'step', onChange, 'aria-valuetext': ariaValuetext, ariaValuetextX, ariaValuetextY, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledby, xName, yName, disabled = false, ...props }, refIn) {
    const ref = useSharedRef(refIn);
    const min = resolveVector(minIn);
    const max = resolveVector(maxIn);
    const step = resolveVector(stepIn);
    const bigStep = resolveVector(bigStepIn);
    const value = clamp(valueIn, min, max);
    const percent = computePercent(value, min, max);
    const roleDescription = useString('slider2dRoleDescription');
    const { focusableInput, onKeyDown, onKeyUp, onChange: onInputChange, onFocus, onBlur, } = useSlider2DKeyboard({
        value,
        min,
        max,
        step,
        bigStep,
        disabled,
        onChange,
        rootRef: ref,
    });
    const [dragging, getDragProps] = useDragV2({
        disabled,
        onDragStart() {
            return { rect: ref.current.getBoundingClientRect(), startValue: value };
        },
        onDrag({ event: e }, { rect, startValue }) {
            // Escape / pointercancel mid-drag: restore the pre-drag value instead of committing.
            if (e.cancelled) {
                onChange(startValue, { event: e, commit: true });
                return;
            }
            const pct = computePercentFromRect(e, rect);
            const newValue = precision === 'infinite'
                ? computeValueRaw(pct, min, max)
                : computeValue(pct, min, max, step);
            if (e.commit) {
                onChange(newValue, { event: e, commit: true });
            }
            else if (!equal(newValue, value)) {
                onChange(newValue, { event: e, commit: false });
            }
        },
    });
    const ctxValue = useMemo(() => ({ percent }), [percent]);
    return (jsx(Slider2DContext.Provider, { value: ctxValue, children: jsxs("div", { "data-fpl-component": "primitive", ...getDragProps({
                ...props,
                className: clsx(className, root),
            }), ref: ref, role: "group", tabIndex: -1, onFocus: (e) => {
                // When the root div itself receives focus (e.g. after a click), redirect
                // focus to the appropriate hidden input for keyboard navigation.
                if (e.target === ref.current) {
                    const input = ref.current.querySelector('input[tabindex="0"]');
                    input?.focus();
                }
            }, onBlur: onBlur, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-disabled": disabled || undefined, "data-dragging": dragging || undefined, children: [children, jsx(HiddenInputs, { "aria-roledescription": roleDescription, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-valuetext": ariaValuetext, ariaValuetextX: ariaValuetextX, ariaValuetextY: ariaValuetextY, min: min, max: max, step: step, value: value, percent: percent, disabled: disabled, focusableInput: focusableInput, xName: xName, yName: yName, onChange: onInputChange, onKeyDown: onKeyDown, onKeyUp: onKeyUp, onFocus: onFocus })] }) }));
});
Root.displayName = 'Slider2DPrimitive.Root';
/**
 * The draggable thumb indicator positioned within the Root.
 * Positioned absolutely based on the current value as a percentage.
 */
function Thumb({ className, style, ...props }) {
    const { percent } = useValidatedContext(Slider2DContext, 'Thumb', 'Slider2DPrimitive.Root');
    return (jsx("div", { "data-fpl-component": "primitive", ...props, className: clsx(thumb, className), style: {
            ...style,
            ...toTopLeftPercent(percent),
        } }));
}
Thumb.displayName = 'Slider2DPrimitive.Thumb';
// eslint-disable-next-line react/display-name
function HiddenInputs(props) {
    const { 'aria-valuetext': ariaValuetext, ariaValuetextX, ariaValuetextY, focusableInput, xName, yName, ...rest } = props;
    // In 'initial' (both) mode, both inputs read the combined valuetext so the
    // first announcement describes the full 2D position. Once we've entered
    // single mode on either axis, both inputs fall back to their own per-axis
    // text so swiping between them narrates each axis independently.
    const singleMode = focusableInput !== 'initial';
    const xValuetext = singleMode ? (ariaValuetextX ?? ariaValuetext) : ariaValuetext;
    const yValuetext = singleMode ? (ariaValuetextY ?? ariaValuetext) : ariaValuetext;
    // In 'initial' state, the X input is the tab stop.
    const focusableAxis = focusableInput === 'initial' ? 'x' : focusableInput;
    return (jsxs(Fragment, { children: [jsx(HiddenInput, { "data-fpl-component": "primitive", ...rest, axis: "x", focusableAxis: focusableAxis, name: xName, "aria-valuetext": xValuetext }), jsx(HiddenInput, { "data-fpl-component": "primitive", ...rest, axis: "y", focusableAxis: focusableAxis, name: yName, "aria-valuetext": yValuetext })] }));
}
// eslint-disable-next-line react/display-name
function HiddenInput({ axis, min, max, step, value, percent, disabled, focusableAxis, ...props }) {
    const focusable = focusableAxis === axis;
    return (jsx("input", { "data-fpl-component": "primitive", ...props, "data-axis": axis, type: "range", className: focusable ? hiddenInput : hiddenInputSrOnly, style: focusable ? toTopLeftPercent(percent) : undefined, "aria-orientation": axis === 'x' ? 'horizontal' : 'vertical', "aria-valuemin": min[axis], "aria-valuemax": max[axis], "aria-valuenow": value[axis], "aria-disabled": disabled || undefined, tabIndex: focusable ? 0 : -1, value: value[axis], min: min[axis], max: max[axis], step: step[axis] }));
}

export { Root, Thumb };
//# sourceMappingURL=slider-2d-primitive.js.map
